/**
 * Pro 2 collection: the Mobile Dock Mini walkthrough from viture.com/pro2 (its "s22").
 * As the section scrolls in, a light band folds up out of the way. Desktop: once the section is 45%
 * down the viewport the glow, collection block and first slide are revealed; then the section pins
 * for five screens while scroll plays the slides — each product shot swings out left as the next
 * swings in, copy lifts out and in, headline words rise and the counter rolls — ending on the
 * adapter with its note. The arrow buttons scroll to the previous or next slide. Mobile doesn't pin:
 * its reveal starts at 78%, and the arrow steps round the four slides.
 * Timeline positions, durations, distances and easing are taken from the source page.
 */
if (!customElements.get('pro2-collection')) {
  const MOBILE_QUERY = '(max-width: 640px)';
  const STAGES = { desktop: { width: 1920, height: 1080 }, mobile: { width: 375, height: 812 } };
  // Time constant (ms) of the lag behind the scroll position, matching the source's scrub: 0.6
  const SMOOTHING_MS = 200;
  // The pinned timeline runs from slide 1 (0) to the closing note (4)
  const LAST_STEP = 4;
  const STEP_SCROLL_MS = 1100;
  const WORD_STAGGER = 0.075;
  // Height of one number in the desktop counter
  const COUNTER_SLOT = 28;

  /** @typedef {(progress: number) => number} Ease */

  /** Eases as GSAP names them; power1 is quadratic, power2 cubic, power3 quartic */
  const EASE = {
    /** @type {Ease} */ none: (progress) => progress,
    /** @type {Ease} */ power1In: (progress) => progress ** 2,
    /** @type {Ease} */ power1Out: (progress) => 1 - (1 - progress) ** 2,
    /** @type {Ease} */ power2In: (progress) => progress ** 3,
    /** @type {Ease} */ power2Out: (progress) => 1 - (1 - progress) ** 3,
    /** @type {Ease} */
    power2InOut: (progress) => (progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2),
    /** @type {Ease} */ power3Out: (progress) => 1 - (1 - progress) ** 4,
  };

  /** @typedef {'x' | 'y' | 'rotation' | 'scaleY' | 'top' | 'opacity' | 'autoAlpha'} TweenProperty */

  /**
   * @typedef {object} Tween
   * @property {number} start
   * @property {number} duration
   * @property {number} from
   * @property {number} to
   * @property {Ease} ease
   */

  /**
   * @param {number} value
   * @returns {number}
   */
  const clamp01 = (value) => Math.min(1, Math.max(0, value));

  /**
   * A scrubbable timeline. Each element property keeps its tweens in start order; at any time it
   * takes its value from the latest tween that has started, held at that tween's end once it is over,
   * and from the first tween's start value before any has begun, as a GSAP timeline renders.
   */
  class Timeline {
    /** @type {Map<HTMLElement, Map<TweenProperty, Tween[]>>} */
    tracks = new Map();
    duration = 0;

    /**
     * @param {HTMLElement | null | undefined} element
     * @param {Partial<Record<TweenProperty, [number, number]>>} properties From and to values
     * @param {number} start
     * @param {number} duration
     * @param {Ease} [ease]
     * @returns {this}
     */
    add(element, properties, start, duration, ease = EASE.none) {
      if (!element) return this;
      const track = this.tracks.get(element) ?? new Map();
      this.tracks.set(element, track);
      Object.entries(properties).forEach(([name, values]) => {
        const property = /** @type {TweenProperty} */ (name);
        const [from, to] = /** @type {[number, number]} */ (values);
        /** @type {Tween[]} */
        const tweens = track.get(property) ?? [];
        tweens.push({ start, duration, from, to, ease });
        tweens.sort((first, second) => first.start - second.start);
        track.set(property, tweens);
      });
      this.duration = Math.max(this.duration, start + duration);
      return this;
    }

    /** @param {number} time */
    render(time) {
      this.tracks.forEach((track, element) => {
        /** @type {Partial<Record<TweenProperty, number>>} */
        const values = {};
        track.forEach((tweens, property) => {
          let active = tweens[0];
          tweens.forEach((tween) => {
            if (tween.start <= time) active = tween;
          });
          if (!active) return;
          const progress = active.duration > 0 ? clamp01((time - active.start) / active.duration) : 1;
          values[property] = active.from + (active.to - active.from) * active.ease(progress);
        });
        applyValues(element, values);
      });
    }
  }

  /**
   * @param {HTMLElement} element
   * @param {Partial<Record<TweenProperty, number>>} values
   */
  const applyValues = (element, values) => {
    const { x, y, rotation, scaleY, top, opacity, autoAlpha } = values;
    if (x !== undefined || y !== undefined || rotation !== undefined || scaleY !== undefined) {
      let transform = `translate(${(x ?? 0).toFixed(2)}px, ${(y ?? 0).toFixed(2)}px)`;
      if (rotation !== undefined) transform += ` rotate(${rotation.toFixed(3)}deg)`;
      if (scaleY !== undefined) transform += ` scaleY(${scaleY.toFixed(4)})`;
      element.style.transform = transform;
    }
    if (top !== undefined) element.style.top = `${top.toFixed(2)}px`;
    if (opacity !== undefined) element.style.opacity = opacity.toFixed(3);
    // autoAlpha hides the element outright at zero, so it can't be seen or clicked
    if (autoAlpha !== undefined) {
      element.style.opacity = autoAlpha.toFixed(3);
      element.style.visibility = autoAlpha > 0 ? 'inherit' : 'hidden';
    }
  };

  /**
   * Wrap each word of a heading in a span, keeping line breaks
   * @param {HTMLElement} heading
   * @returns {HTMLElement[]}
   */
  const splitWords = (heading) => {
    /** @type {HTMLElement[]} */
    const words = [];
    const firstDelay = Number(heading.dataset.delay) || 0;
    Array.from(heading.childNodes).forEach((node) => {
      const text = node.nodeType === Node.TEXT_NODE ? node.nodeValue ?? '' : '';
      if (!text.trim()) return;
      const fragment = document.createDocumentFragment();
      text.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          fragment.append(document.createTextNode(part));
          return;
        }
        const word = document.createElement('span');
        word.className = 'pro2-collection__word';
        word.textContent = part;
        word.style.setProperty('--word-delay', `${(firstDelay + words.length * WORD_STAGGER).toFixed(3)}s`);
        words.push(word);
        fragment.append(word);
      });
      node.replaceWith(fragment);
    });
    heading.classList.add('is-split');
    return words;
  };

  /**
   * Give each word its slice of one gradient spanning the whole heading
   * @param {HTMLElement} heading
   */
  const paintGradient = (heading) => {
    if (!heading.offsetWidth) return;
    const gradientSize = `${heading.offsetWidth}px 100%`;
    heading.querySelectorAll('.pro2-collection__word').forEach((element) => {
      const word = /** @type {HTMLElement} */ (element);
      // The heading is positioned, so offsets are from it; take off the reveal's slide-in `left`
      const offsetInHeading = word.offsetLeft - (parseFloat(getComputedStyle(word).left) || 0);
      word.style.backgroundSize = gradientSize;
      word.style.backgroundPosition = `${-offsetInHeading}px 0`;
    });
  };

  class Pro2Collection extends HTMLElement {
    /** @type {MediaQueryList} */
    mobileQuery = window.matchMedia(MOBILE_QUERY);
    /** @type {HTMLElement} */
    track = document.createElement('div');
    /** @type {HTMLElement} */
    sticky = document.createElement('div');
    /** @type {HTMLElement[]} */
    headings = [];
    /** @type {HTMLElement[]} */
    folds = [];
    desktopTimeline = new Timeline();
    /** Timeline time being drawn, easing toward `targetTime` */
    currentTime = 0;
    targetTime = 0;
    /** @type {number | null} */
    animationFrame = null;
    /** @type {number | null} */
    lastTickTime = null;
    /** @type {number | null} */
    stepScrollFrame = null;
    /** @type {HTMLElement[]} */
    mobileImages = [];
    /** @type {HTMLElement[]} */
    mobileCopies = [];
    mobileSlide = 0;
    /** @type {{ timeline: Timeline, startTime: number, frame: number } | null} */
    mobileTransition = null;
    resizeFrame = 0;
    /** @type {IntersectionObserver[]} */
    observers = [];

    connectedCallback() {
      this.track = /** @type {HTMLElement} */ (this.querySelector('.pro2-collection__track'));
      this.sticky = /** @type {HTMLElement} */ (this.querySelector('.pro2-collection__sticky'));
      this.folds = Array.from(this.querySelectorAll('.pro2-collection__fold'));
      const desktop = /** @type {HTMLElement} */ (this.querySelector('.pro2-collection__stage--d'));
      const mobile = /** @type {HTMLElement} */ (this.querySelector('.pro2-collection__stage--m'));

      // Split every heading the timelines animate by word, so each carries its gradient slice
      /** @type {Map<HTMLElement, HTMLElement[]>} */
      const wordsByHeading = new Map();
      this.headings = Array.from(this.querySelectorAll('[data-split]'));
      this.headings.forEach((heading) => wordsByHeading.set(heading, splitWords(heading)));
      this.#buildDesktopTimeline(desktop, wordsByHeading);
      this.mobileImages = Array.from(mobile.querySelectorAll('[data-slide-image]'));
      this.mobileCopies = Array.from(mobile.querySelectorAll('[data-slide-copy]'));

      this.classList.add('is-armed');
      this.#observe('is-intro-d', '0px 0px -55% 0px');
      this.#observe('is-intro-m', '0px 0px -22% 0px');
      this.#observe('is-content-m', '0px 0px -60% 0px');

      desktop.querySelector('.pro2-collection__btn--next')?.addEventListener('click', this.onDesktopNext);
      desktop.querySelector('.pro2-collection__btn--prev')?.addEventListener('click', this.onDesktopPrev);
      mobile.querySelector('.pro2-collection__btn--next')?.addEventListener('click', this.onMobileNext);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.mobileQuery.addEventListener('change', this.onResize);
      document.fonts?.ready.then(() => this.headings.forEach(paintGradient));

      this.#fitStage();
      this.headings.forEach(paintGradient);
      this.targetTime = this.#readPinProgress() * LAST_STEP;
      this.currentTime = this.targetTime;
      this.desktopTimeline.render(this.currentTime);
      this.#renderFolds();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.mobileQuery.removeEventListener('change', this.onResize);
      this.observers.forEach((observer) => observer.disconnect());
      cancelAnimationFrame(this.resizeFrame);
      if (this.animationFrame !== null) cancelAnimationFrame(this.animationFrame);
      if (this.stepScrollFrame !== null) cancelAnimationFrame(this.stepScrollFrame);
      if (this.mobileTransition) cancelAnimationFrame(this.mobileTransition.frame);
      this.animationFrame = null;
    }

    onResize = () => {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.#fitStage();
        this.headings.forEach(paintGradient);
        this.onScroll();
      });
    };

    onScroll = () => {
      this.#renderFolds();
      if (this.mobileQuery.matches) return;
      // Entering the pin finishes the intro at once, as the source does
      if (this.track.getBoundingClientRect().top <= 0 && !this.classList.contains('is-intro-d')) {
        this.#finishReveal('is-intro-d');
      }
      this.targetTime = this.#readPinProgress() * LAST_STEP;
      if (this.animationFrame === null) this.animationFrame = requestAnimationFrame(this.tick);
    };

    /** Ease the drawn timeline toward the scroll position, then stop once it has caught up */
    tick = (/** @type {number} */ now) => {
      const elapsed = Math.min(now - (this.lastTickTime ?? now), 64);
      this.lastTickTime = now;
      this.currentTime += (this.targetTime - this.currentTime) * (1 - Math.exp(-elapsed / SMOOTHING_MS));
      if (Math.abs(this.targetTime - this.currentTime) < 0.0005) this.currentTime = this.targetTime;
      this.desktopTimeline.render(this.currentTime);

      if (this.currentTime === this.targetTime) {
        this.animationFrame = null;
        this.lastTickTime = null;
      } else {
        this.animationFrame = requestAnimationFrame(this.tick);
      }
    };

    onDesktopNext = () => this.#scrollToStep(Math.round(this.currentTime) + 1);

    onDesktopPrev = () => this.#scrollToStep(Math.round(this.currentTime) - 1);

    /** Step round the four slides: the shot swings out left as the next swings in, copy lifts over */
    onMobileNext = () => {
      if (this.mobileTransition) {
        cancelAnimationFrame(this.mobileTransition.frame);
        this.mobileTransition.timeline.render(this.mobileTransition.timeline.duration);
      }
      this.#finishReveal('is-intro-m');

      const leaving = this.mobileSlide;
      this.mobileSlide = (this.mobileSlide + 1) % this.mobileImages.length;
      const entering = this.mobileSlide;
      const timeline = new Timeline()
        .add(this.mobileImages[leaving], { x: [0, -140], rotation: [0, -5] }, 0, 0.34, EASE.power2In)
        .add(this.mobileImages[leaving], { autoAlpha: [1, 0] }, 0, 0.32, EASE.power1In)
        .add(this.mobileImages[entering], { x: [140, 0], rotation: [4, 0] }, 0.3, 0.42, EASE.power3Out)
        .add(this.mobileImages[entering], { autoAlpha: [0, 1] }, 0.3, 0.24, EASE.power1Out)
        .add(this.mobileCopies[leaving], { y: [0, -50], autoAlpha: [1, 0] }, 0.04, 0.24, EASE.power2In)
        .add(this.mobileCopies[entering], { y: [50, 0], autoAlpha: [0, 1] }, 0.42, 0.3, EASE.power3Out);

      const transition = { timeline, startTime: performance.now(), frame: 0 };
      /** @param {number} now */
      const play = (now) => {
        const time = (now - transition.startTime) / 1000;
        timeline.render(Math.min(time, timeline.duration));
        if (time < timeline.duration) {
          transition.frame = requestAnimationFrame(play);
        } else if (this.mobileTransition === transition) {
          this.mobileTransition = null;
        }
      };
      timeline.render(0);
      transition.frame = requestAnimationFrame(play);
      this.mobileTransition = transition;
    };

    /**
     * Build the pinned scroll timeline, with times in slides: slide n is fully in at time n - 1
     * @param {HTMLElement} stage
     * @param {Map<HTMLElement, HTMLElement[]>} wordsByHeading
     */
    #buildDesktopTimeline(stage, wordsByHeading) {
      const images = /** @type {HTMLElement[]} */ (Array.from(stage.querySelectorAll('[data-slide-image]')));
      const copies = /** @type {HTMLElement[]} */ (Array.from(stage.querySelectorAll('[data-slide-copy]')));
      const counter = /** @type {HTMLElement | null} */ (stage.querySelector('.pro2-collection__counter-strip'));
      const note = /** @type {HTMLElement | null} */ (stage.querySelector('.pro2-collection__anno'));
      const next = /** @type {HTMLElement | null} */ (stage.querySelector('.pro2-collection__btn--next'));
      const prev = /** @type {HTMLElement | null} */ (stage.querySelector('.pro2-collection__btn--prev'));
      const orangeSkin = /** @type {HTMLElement | null} */ (stage.querySelector('.pro2-collection__btn-skin--orange'));
      const darkSkin = /** @type {HTMLElement | null} */ (stage.querySelector('.pro2-collection__btn-skin--dark'));
      const timeline = this.desktopTimeline;

      // Slides 1–3 each hand over to the next
      for (let slide = 0; slide < 3; slide++) {
        const start = slide + 0.225;
        timeline
          .add(images[slide], { x: [0, -420], rotation: [0, -5] }, start, 0.26, EASE.power2In)
          .add(images[slide], { autoAlpha: [1, 0] }, start, 0.25, EASE.power1In)
          .add(images[slide + 1], { x: [420, 0], rotation: [4, 0] }, start + 0.24, 0.31, EASE.power3Out)
          .add(images[slide + 1], { autoAlpha: [0, 1] }, start + 0.24, 0.18, EASE.power1Out)
          .add(copies[slide], { y: [0, -70], autoAlpha: [1, 0] }, start + 0.03, 0.18, EASE.power2In)
          .add(copies[slide + 1], { y: [70, 0], autoAlpha: [0, 1] }, start + 0.32, 0.22, EASE.power3Out)
          .add(counter, { y: [-COUNTER_SLOT * slide, -COUNTER_SLOT * (slide + 1)] }, start + 0.12, 0.3, EASE.power2InOut);

        // The incoming headline's words rise in across a short window
        const heading = /** @type {HTMLElement | null} */ (copies[slide + 1]?.querySelector('[data-split]') ?? null);
        const words = heading ? wordsByHeading.get(heading) ?? [] : [];
        const wordWindow = 0.22;
        const wordDuration = wordWindow * 0.6;
        const wordStagger = words.length > 1 ? (wordWindow - wordDuration) / (words.length - 1) : 0;
        words.forEach((word, index) => {
          timeline.add(word, { opacity: [0, 1], top: [26, 0] }, start + 0.32 + wordStagger * index, wordDuration, EASE.power2Out);
        });
      }

      // Slide 4 makes way for the adapter shot and its note; the next button bows out
      timeline
        .add(images[3], { x: [0, -160], rotation: [0, -2] }, 3.225, 0.26, EASE.power2In)
        .add(images[3], { autoAlpha: [1, 0] }, 3.225, 0.25, EASE.power1In)
        .add(images[4], { x: [160, 0] }, 3.465, 0.31, EASE.power3Out)
        .add(images[4], { autoAlpha: [0, 1] }, 3.465, 0.18, EASE.power1Out)
        .add(next, { autoAlpha: [1, 0] }, 3.225, 0.15)
        .add(note, { autoAlpha: [0, 1], y: [30, 0] }, 3.645, 0.2, EASE.power3Out)
        // After the first slide the next button shows its progress ring and the back button appears
        .add(orangeSkin, { opacity: [1, 0] }, 0.225, 0.3)
        .add(darkSkin, { opacity: [0, 1] }, 0.225, 0.3)
        .add(prev, { autoAlpha: [0, 1] }, 0.225, 0.3);
      timeline.duration = LAST_STEP;
    }

    /**
     * Scroll smoothly to where the pinned timeline shows the given slide
     * @param {number} step
     */
    #scrollToStep(step) {
      const pinDistance = this.track.offsetHeight - this.sticky.offsetHeight;
      const pinStart = window.scrollY + this.track.getBoundingClientRect().top;
      const target = pinStart + (pinDistance * Math.max(0, Math.min(LAST_STEP, step))) / LAST_STEP;
      const from = window.scrollY;
      const startTime = performance.now();
      if (this.stepScrollFrame !== null) cancelAnimationFrame(this.stepScrollFrame);
      /** @param {number} now */
      const scroll = (now) => {
        const progress = clamp01((now - startTime) / STEP_SCROLL_MS);
        window.scrollTo({ top: from + (target - from) * EASE.power2InOut(progress), behavior: 'instant' });
        this.stepScrollFrame = progress < 1 ? requestAnimationFrame(scroll) : null;
      };
      this.stepScrollFrame = requestAnimationFrame(scroll);
    }

    /** @returns {number} 0–1 across the pinned scroll */
    #readPinProgress() {
      const pinDistance = this.track.offsetHeight - this.sticky.offsetHeight;
      const top = this.track.getBoundingClientRect().top;
      if (pinDistance <= 0) return top <= 0 ? 1 : 0;
      return clamp01(-top / pinDistance);
    }

    /** Fold the light band up as the section top travels from the viewport bottom to its top */
    #renderFolds() {
      const isMobile = this.mobileQuery.matches;
      const progress = clamp01((window.innerHeight - this.track.getBoundingClientRect().top) / window.innerHeight);
      const fullScale = isMobile ? 0.16 : 0.14;
      const fadeStart = isMobile ? 0.65 : 0.7;
      const scale = 1 - (1 - fullScale) * EASE.power3Out(progress);
      const opacity = 1 - EASE.power2In(clamp01((progress - fadeStart) / (1 - fadeStart)));
      this.folds.forEach((fold) => applyValues(fold, { scaleY: scale, autoAlpha: opacity }));
    }

    /**
     * Add a class once the section top passes the given point, starting a CSS reveal
     * @param {string} className
     * @param {string} rootMargin
     */
    #observe(className, rootMargin) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.classList.add(className);
          observer.disconnect();
        },
        { rootMargin }
      );
      observer.observe(this);
      this.observers.push(observer);
    }

    /**
     * Show a reveal's end state straight away, skipping its transitions
     * @param {string} className
     */
    #finishReveal(className) {
      if (this.classList.contains(className)) return;
      this.classList.add('is-instant', className);
      // Let the end state paint without transitions before handing styling back
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.remove('is-instant')));
    }

    /** Desktop covers the viewport; mobile is 375 wide and as tall as the viewport at that width */
    #fitStage() {
      const viewportWidth = window.innerWidth || this.sticky.clientWidth;
      const viewportHeight = this.sticky.clientHeight;
      if (this.mobileQuery.matches) {
        const scale = viewportWidth / STAGES.mobile.width;
        this.style.setProperty('--stage-h', (viewportHeight / scale).toFixed(2));
        this.style.setProperty('--scale', scale.toFixed(4));
      } else {
        const { width, height } = STAGES.desktop;
        const scale = Math.max(viewportWidth / width, viewportHeight / height);
        this.style.setProperty('--stage-h', String(height));
        this.style.setProperty('--scale', scale.toFixed(4));
      }
    }
  }

  customElements.define('pro2-collection', Pro2Collection);
}
